import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export interface TelegramTabBarProps {
  cartCount: number;
}

const navItems = [
  { to: '/', icon: 'ri-home-5-line', label: 'Главная' },
  { to: '/catalog', icon: 'ri-store-2-line', label: 'Каталог' },
  { to: '/services', icon: 'ri-service-line', label: 'Пасека под ключ' },
];

const moreItems = [
  { to: '/lk-orders', icon: 'ri-file-list-3-line', label: 'Заказы' },
  { to: '/delivery', icon: 'ri-truck-line', label: 'Доставка' },
  { to: '/about', icon: 'ri-store-3-line', label: 'О нас' },
  { to: '/contacts', icon: 'ri-message-3-line', label: 'Контакты' },
];

function tabClass(isActive: boolean) {
  return [
    'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-1.5 text-[11px] font-medium transition-colors',
    isActive ? 'text-honey-dark bg-honey/10' : 'text-dark/55 hover:bg-dark/5 hover:text-dark',
  ].join(' ');
}

export default function TelegramTabBar({ cartCount }: TelegramTabBarProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const moreRef = useRef<HTMLDivElement>(null);
  const isMoreActive = moreItems.some((item) => location.pathname === item.to);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;

    const handleClick = (event: MouseEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [moreOpen]);

  return (
    <nav className="telegram-tabbar fixed bottom-0 left-0 right-0 z-50 border-t border-sand/70 bg-card/95 px-2 pt-2 shadow-[0_-8px_28px_rgba(42,26,10,0.08)] backdrop-blur-md">
      <div ref={moreRef} className="relative mx-auto flex max-w-md items-center gap-1">
        {moreOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-2 rounded-2xl border border-sand/70 bg-card p-2 shadow-[0_-10px_30px_rgba(42,26,10,0.12)]">
            <div className="grid grid-cols-3 gap-1">
              {moreItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-2 py-3 text-center text-[11px] font-medium text-dark/65 transition-colors hover:bg-honey/10 hover:text-honey-dark"
                >
                  <i className={`${item.icon} text-xl leading-none`} />
                  <span className="max-w-full truncate">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => tabClass(isActive)}>
            <i className={`${item.icon} text-lg leading-none`} />
            <span className="max-w-full truncate">{item.label}</span>
          </NavLink>
        ))}

        <NavLink
          to="/cart"
          className={({ isActive }) => [
            tabClass(isActive),
            'relative',
          ].join(' ')}
        >
          <i className="ri-shopping-bag-3-line text-lg leading-none" />
          <span className="max-w-full truncate">Корзина</span>
          {cartCount > 0 && (
            <span className="absolute right-2 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-honey px-1 text-[10px] font-semibold leading-none text-white">
              {cartCount}
            </span>
          )}
        </NavLink>

        <button
          type="button"
          onClick={() => setMoreOpen((open) => !open)}
          className={tabClass(isMoreActive || moreOpen)}
          aria-expanded={moreOpen}
          aria-label="Открыть дополнительные разделы"
        >
          <i className="ri-more-2-line text-lg leading-none" />
          <span className="max-w-full truncate">Еще</span>
        </button>
      </div>
    </nav>
  );
}
